import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import {
  fetchRecycleBin,
  restoreRecycleBinItems,
  permanentDeleteRecycleBinItems,
  emptyRecycleBin,
  RecycleBinItem,
  RecycleBinStats,
} from '../../store/actions/RecycleBinActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import Table from '../shared/TailwindComponents/Table';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import Badge from '../shared/TailwindComponents/Badge';
import {
  PiTrashLight,
  PiArrowCounterClockwiseLight,
  PiFileLight,
  PiFolderLight,
  PiBriefcaseLight,
  PiUserListLight,
  PiCheckSquareOffsetLight,
  PiUsersThreeLight,
  PiBuildingLight,
  PiCalendarLight,
  PiTableLight,
  PiFileMagnifyingGlassLight,
  PiListMagnifyingGlassLight,
  PiWarningCircleLight,
  PiRecycleFill,
} from 'react-icons/pi';
import { formatDateTime } from '../../utils/functions';
import RecycleBinDeleteConfirmation from './RecycleBinDeleteConfirmation';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, recycleBinTourSteps } from '../shared/FeatureTour';

// Map entity_type to display config
const ENTITY_CONFIG: Record<string, { label: string; icon: React.ReactElement; color: string }> = {
  file: { label: 'File', icon: <PiFileLight className="w-4 h-4" />, color: 'gray' },
  folder: { label: 'Folder', icon: <PiFolderLight className="w-4 h-4" />, color: 'blue' },
  client: { label: 'Client', icon: <PiUserListLight className="w-4 h-4" />, color: 'purple' },
  matter: { label: 'Matter', icon: <PiBriefcaseLight className="w-4 h-4" />, color: 'indigo' },
  task: { label: 'Task', icon: <PiCheckSquareOffsetLight className="w-4 h-4" />, color: 'green' },
  team: { label: 'Team', icon: <PiUsersThreeLight className="w-4 h-4" />, color: 'teal' },
  department: { label: 'Department', icon: <PiBuildingLight className="w-4 h-4" />, color: 'orange' },
  calendar_event: { label: 'Calendar Event', icon: <PiCalendarLight className="w-4 h-4" />, color: 'red' },
  table: { label: 'Table', icon: <PiTableLight className="w-4 h-4" />, color: 'cyan' },
  review: { label: 'Review', icon: <PiFileMagnifyingGlassLight className="w-4 h-4" />, color: 'amber' },
  review_session: { label: 'Review Session', icon: <PiFileMagnifyingGlassLight className="w-4 h-4" />, color: 'amber' },
  research: { label: 'Research', icon: <PiListMagnifyingGlassLight className="w-4 h-4" />, color: 'violet' },
  draft: { label: 'Draft', icon: <PiFileLight className="w-4 h-4" />, color: 'slate' },
  insight: { label: 'Insight', icon: <PiFileLight className="w-4 h-4" />, color: 'lime' },
};

const RecycleBinPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const isAdmin = !!userInfo?.is_org_admin;

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'recycle-bin',
    steps: recycleBinTourSteps,
    autoStartDelay: 600,
  });

  const [items, setItems] = useState<RecycleBinItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [retentionDays, setRetentionDays] = useState(30);
  const [stats, setStats] = useState<RecycleBinStats>({});
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('deleted_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 50,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
  });

  const activeTypeFilter = selectedFilters.entity_type?.length === 1 ? selectedFilters.entity_type[0] : undefined;

  const loadItems = useCallback(async (page = 1, append = false) => {
    setIsLoading(true);
    try {
      const result = await dispatch(fetchRecycleBin({
        page,
        per_page: 50,
        entity_type: activeTypeFilter || undefined,
        search: searchTerm || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        user_only: true,
      }) as any);

      if (result?.success) {
        const pageItems: RecycleBinItem[] = result.items || [];
        setItems(prev => append ? [...prev, ...pageItems] : pageItems);
        setRetentionDays(result.retention_days || 30);
        setStats(result.stats || {});
        if (result.pagination) {
          setPagination(result.pagination);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, activeTypeFilter, searchTerm, sortBy, sortOrder]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const getItemKey = (item: RecycleBinItem): string => {
    return `${item.entity_type}:${item.uuid || item.id}`;
  };

  const handleRestore = async (itemsToRestore?: RecycleBinItem[]) => {
    const targets = itemsToRestore || items.filter(i => selectedItems.includes(getItemKey(i)));
    const restorePayload = targets.map(i => ({
      entity_type: i.entity_type,
      id: i.uuid || i.id,
    }));

    setIsLoading(true);
    try {
      const success = await dispatch(restoreRecycleBinItems(restorePayload) as any);
      if (success) {
        setSelectedItems([]);
        await loadItems(1);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePermanentDelete = async (itemsToDelete?: RecycleBinItem[]) => {
    const targets = itemsToDelete || items.filter(i => selectedItems.includes(getItemKey(i)));
    const deletePayload = targets.map(i => ({
      entity_type: i.entity_type,
      id: i.uuid || i.id,
    }));

    dispatch(
      showModal({
        type: 'custom',
        message: t('Permanently Delete'),
        icon: <PiTrashLight className="w-6 h-6 text-red-600" />,
        content: (
          <RecycleBinDeleteConfirmation
            itemCount={targets.length}
            onConfirm={async () => {
              setIsLoading(true);
              try {
                const success = await dispatch(permanentDeleteRecycleBinItems(deletePayload) as any);
                if (success) {
                  setSelectedItems([]);
                  await loadItems(1);
                }
              } finally {
                setIsLoading(false);
                dispatch(hideModal());
              }
            }}
            onCancel={() => dispatch(hideModal())}
          />
        ),
        onCancel: () => {},
        showModal: true,
      })
    );
  };

  const handleEmptyAll = () => {
    dispatch(
      showModal({
        type: 'custom',
        message: t('Empty Recycle Bin'),
        icon: <PiRecycleFill className="w-6 h-6 text-red-600" />,
        content: (
          <RecycleBinDeleteConfirmation
            itemCount={pagination.total_items}
            isEmptyAll
            onConfirm={async () => {
              setIsLoading(true);
              try {
                const success = await dispatch(emptyRecycleBin() as any);
                if (success) {
                  setItems([]);
                  setSelectedItems([]);
                  setStats({});
                  setPagination(p => ({ ...p, total_items: 0, total_pages: 1, has_next: false }));
                }
              } finally {
                setIsLoading(false);
                dispatch(hideModal());
              }
            }}
            onCancel={() => dispatch(hideModal())}
          />
        ),
        onCancel: () => {},
        showModal: true,
      })
    );
  };

  const getEntityConfig = (type: string) => {
    return ENTITY_CONFIG[type] || { label: type, icon: <PiFileLight className="w-4 h-4" />, color: 'gray' };
  };

  const expiringItems = items.filter(i => i.days_remaining <= 7 && i.days_remaining > 0);

  // Build entity type filter options from stats
  const typeFilterOptions = Object.entries(stats)
    .filter(([, count]) => count > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([type]) => ({
      value: type,
      label: getEntityConfig(type).label,
    }));

  // Apply client-side filtering when multiple types selected
  const selectedTypes = selectedFilters.entity_type || [];
  const filteredItems = selectedTypes.length > 1
    ? items.filter(i => selectedTypes.includes(i.entity_type))
    : items;

  // Build table data
  const columns = ['Select', 'Name', 'Type', 'Created By', 'Deleted By', 'Deleted', 'Days Remaining', 'Actions'];

  const tableData = filteredItems.map((item) => {
    const config = getEntityConfig(item.entity_type);
    return {
      id: getItemKey(item),
      doc: item,
      Select: null,
      Name: (
        <div className="flex items-center space-x-2">
          <span className="text-gray-400 flex-shrink-0">{config.icon}</span>
          <span className="truncate" title={item.name}>
            {item.name}
          </span>
        </div>
      ),
      Type: (
        <Badge color={config.color as any} className="text-xs whitespace-nowrap">
          {t(config.label)}
        </Badge>
      ),
      'Created By': item.created_by_name ? (
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-gray-700 truncate">{item.created_by_name}</span>
          <Badge color={item.created_by_role === 'Admin' ? 'amber' : 'gray'} className="text-[10px] leading-tight px-1.5 py-0">
            {item.created_by_role}
          </Badge>
        </div>
      ) : (
        <span className="text-sm text-gray-400">—</span>
      ),
      'Deleted By': item.deleted_by_name ? (
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-gray-700 truncate">{item.deleted_by_name}</span>
          <Badge color={item.deleted_by_role === 'Admin' ? 'amber' : 'gray'} className="text-[10px] leading-tight px-1.5 py-0">
            {item.deleted_by_role}
          </Badge>
        </div>
      ) : (
        <span className="text-sm text-gray-400">—</span>
      ),
      Deleted: formatDateTime(item.deleted_at),
      'Days Remaining': (
        <span className={`text-sm font-medium ${item.days_remaining <= 7 ? 'text-red-600' : 'text-gray-500'}`}>
          {item.days_remaining} {t('days')}
        </span>
      ),
    };
  });

  const tableActions = [
    {
      label: t('Restore'),
      onClick: (row: any) => handleRestore([row.doc]),
      icon: <PiArrowCounterClockwiseLight className="h-4 w-4" />,
    },
    {
      label: t('Delete Permanently'),
      onClick: (row: any) => handlePermanentDelete([row.doc]),
      icon: <PiTrashLight className="h-4 w-4" />,
      isDestructive: true,
    },
  ];

  const handleSelectItem = (id: string) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const selectableIds = tableData.map(row => row.id);
  const handleSelectAllItems = () => {
    setSelectedItems(selectedItems.length === selectableIds.length ? [] : selectableIds);
  };

  const handleLoadMore = () => {
    if (pagination.has_next) {
      loadItems(pagination.page + 1, true);
    }
  };

  // Build filter bar actions
  const filterActions: { label: string; onClick: () => void; icon?: React.ReactElement }[] = [];
  if (selectedItems.length > 0) {
    filterActions.push({
      label: t('Restore Selected'),
      onClick: () => handleRestore(),
      icon: <PiArrowCounterClockwiseLight className="h-4 w-4" />,
    });
    filterActions.push({
      label: t('Delete Selected'),
      onClick: () => handlePermanentDelete(),
      icon: <PiTrashLight className="h-4 w-4" />,
    });
    if (items.length > 0) {
      filterActions.push({
        label: t('Empty Recycle Bin'),
        onClick: handleEmptyAll,
        icon: <PiTrashLight className="h-4 w-4" />,
      });
    }
  }

  return (
    <>
    <div className="flex flex-col h-full">
      <div data-tour-id="recycle-bin-area">
      <FilterBarWithTabs
        filters={typeFilterOptions.length > 0 ? [{
          id: 'entity_type',
          name: t('Type'),
          options: typeFilterOptions,
        }] : []}
        selectedFilters={selectedFilters}
        onFilterChange={(filterName, values) => {
          setSelectedFilters(prev => ({ ...prev, [filterName]: values }));
          setSelectedItems([]);
        }}
        sortOptions={[
          { value: 'name_asc', label: t('Name (A-Z)'), current: sortBy === 'name' && sortOrder === 'asc' },
          { value: 'name_desc', label: t('Name (Z-A)'), current: sortBy === 'name' && sortOrder === 'desc' },
          { value: 'deleted_at_asc', label: t('Deleted (Oldest)'), current: sortBy === 'deleted_at' && sortOrder === 'asc' },
          { value: 'deleted_at_desc', label: t('Deleted (Newest)'), current: sortBy === 'deleted_at' && sortOrder === 'desc' },
          { value: 'days_remaining_asc', label: t('Expiring Soon'), current: sortBy === 'days_remaining' && sortOrder === 'asc' },
          { value: 'days_remaining_desc', label: t('Expiring Last'), current: sortBy === 'days_remaining' && sortOrder === 'desc' },
        ]}
        onSortChange={(value) => {
          const parts = value.split('_');
          const order = parts.pop() as 'asc' | 'desc';
          const field = parts.join('_');
          setSortBy(field);
          setSortOrder(order);
        }}
        onSearchChange={(query) => {
          if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
          }
          searchDebounceRef.current = setTimeout(() => {
            setSearchTerm(query);
          }, 400);
        }}
        sortValue={`${sortBy}_${sortOrder}`}
        actions={filterActions}
        selectionCount={selectedItems.length > 0 ? selectedItems.length : undefined}
        onClearAllFilters={() => {
          setSearchTerm('');
          setSortBy('deleted_at');
          setSortOrder('desc');
          setSelectedFilters({});
        }}
      />

      {/* Expiring items warning */}
      {expiringItems.length > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-sm">
          <PiWarningCircleLight className="w-4 h-4 flex-shrink-0" />
          <span>
            {expiringItems.length} {expiringItems.length === 1 ? t('item') : t('items')} {t('will be permanently deleted within 7 days')}
          </span>
        </div>
      )}
      </div>

      <div className="flex-1 min-h-0 overflow-auto relative">
        <div className="flex-1 overflow-x-auto overflow-y-auto">
          <Table
            isLoadingMore={isLoading}
            columns={columns}
            data={tableData}
            actions={tableActions}
            enableSelection
            selectedIds={selectedItems}
            onSelect={handleSelectItem}
            onSelectAll={handleSelectAllItems}
            hasMore={pagination.has_next}
            onLoadMore={handleLoadMore}
            resizableColumns={true}
            defaultColumnWidths={{
              'Select': 48,
              'Name': 300,
              'Type': 140,
              'Deleted': 180,
              'Days Remaining': 130,
              'Actions': 48,
            }}
            sortableColumns={['Name', 'Deleted', 'Days Remaining']}
            sortBy={sortBy === 'deleted_at' ? 'deleted' : sortBy === 'days_remaining' ? 'days_remaining' : sortBy}
            sortOrder={sortOrder}
            onSort={(col, order) => {
              const columnMap: Record<string, string> = {
                'name': 'name',
                'deleted': 'deleted_at',
                'days_remaining': 'days_remaining',
              };
              setSortBy(columnMap[col] || col);
              setSortOrder(order);
            }}
            total_items={pagination.total_items}
            total_showing_items={tableData.length}
            max={expiringItems.length > 0 ? 'max-h-[calc(100vh-210px)]' : 'max-h-[calc(100vh-185px)]'}
            min={expiringItems.length > 0 ? 'max-h-[calc(100vh-210px)]' : 'max-h-[calc(100vh-185px)]'}
            setIsShowing={true}
            showingLabel={t('items')}
            infoText={`${t('Automatically deleted after')} ${retentionDays} ${t('days.')}${isAdmin ? ` ${t('You can change this in Organization Settings.')}` : ''}`}
            emptyState={{
              icon: <PiRecycleFill />,
              headline: t('Recycle bin is empty'),
              subtext: t('Deleted items from across the platform will appear here'),
            }}
          />
        </div>
      </div>
    </div>
    <FeatureTour
      steps={recycleBinTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      disableOverlay
    />
    </>
  );
};

export default RecycleBinPage;
